// Єдине сховище автентифікаційного стану клієнта -- ЛИШЕ в пам'яті модуля.
//
// Access-токен і CSRF-токен живуть у змінних цього модуля і зникають разом
// зі вкладкою. У постійне сховище браузера (Web Storage) нічого не пишеться:
// звідти будь-який XSS-скрипт міг би їх просто прочитати і вивезти
// (знахідка FE-01). Після перезавантаження сторінки сесію відновлює
// AuthContext: POST /api/auth/refresh за httpOnly refresh-cookie (JS її не
// бачить) -> новий access-токен -> GET /api/auth/me -> роль.
//
// Контракт сервера (Gurt_Systems_server/routes/auth.js):
//   POST /api/auth/login   {username,password} -> {token, role, csrfToken}
//   GET  /api/auth/csrf    -> {csrfToken}
//   POST /api/auth/refresh (cookie + X-CSRF-Token) -> {token, role, csrfToken}
//   POST /api/auth/logout  (cookie + X-CSRF-Token) -> 200
//   GET  /api/auth/me      (Authorization: Bearer <token>) -> {id, username, role}
//
// credentials:"include" обов'язковий для всіх /api/auth/*-запитів, інакше
// браузер не надішле й не збереже httpOnly-cookie на інший порт/домен.

import axios from "axios";

// Для axios-запитів сторінок: дозволяє надсилати httpOnly-cookie на API.
axios.defaults.withCredentials = true;

let accessToken = null;
let csrfToken = null;
// Спільний проміс поточного оновлення: паралельні 401 (або подвійний
// mount у StrictMode) не мають запускати кілька refresh одночасно -- сервер
// ротує refresh-cookie, і другий запит зі старою cookie отримав би 401.
let refreshInFlight = null;

const resolveBase = (apiBase) =>
  apiBase || process.env.REACT_APP_API_BASE || "";

function syncAxiosHeader() {
  if (!axios.defaults.headers) axios.defaults.headers = {};
  if (!axios.defaults.headers.common) axios.defaults.headers.common = {};
  if (accessToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || null;
  syncAxiosHeader();
}

export function getCsrfToken() {
  return csrfToken;
}

export function setCsrfToken(token) {
  csrfToken = token || null;
}

export function clearAuth() {
  accessToken = null;
  csrfToken = null;
  syncAxiosHeader();
}

// Значення заголовка Authorization для fetch/axios-запитів сторінок.
// undefined, коли сесії немає (axios такий заголовок просто пропускає).
export function getAuthHeader() {
  return accessToken ? `Bearer ${accessToken}` : undefined;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function fetchCsrfToken(base) {
  try {
    const response = await fetch(`${base}/api/auth/csrf`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return null;
    const data = await readJson(response);
    if (data?.csrfToken) {
      csrfToken = data.csrfToken;
      return csrfToken;
    }
    return null;
  } catch {
    return null;
  }
}

// CSRF-токен з пам'яті, а якщо його ще немає (наприклад, одразу після
// перезавантаження сторінки) -- отримуємо новий через GET /api/auth/csrf.
export async function ensureCsrfToken(apiBase) {
  if (csrfToken) return csrfToken;
  return fetchCsrfToken(resolveBase(apiBase));
}

function postWithCsrf(base, path) {
  const headers = {};
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  return fetch(`${base}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
  });
}

async function isCsrfRejection(response) {
  if (response.status !== 403) return false;
  const data = await readJson(
    typeof response.clone === "function" ? response.clone() : response
  );
  return !!data && data.code === "CSRF_TOKEN_INVALID";
}

async function doRefresh(base) {
  try {
    await ensureCsrfToken(base);
    let response = await postWithCsrf(base, "/api/auth/refresh");

    // CSRF-токен у пам'яті застарів або не збігається з cookie -- один раз
    // беремо свіжий і повторюємо запит.
    if (await isCsrfRejection(response)) {
      csrfToken = null;
      await fetchCsrfToken(base);
      response = await postWithCsrf(base, "/api/auth/refresh");
    }

    if (!response.ok) {
      clearAuth();
      return null;
    }

    const data = await readJson(response);
    if (!data?.token) {
      clearAuth();
      return null;
    }

    setAccessToken(data.token);
    if (data.csrfToken) csrfToken = data.csrfToken;
    return data.token;
  } catch {
    return null;
  }
}

// Оновлення access-токена через httpOnly refresh-cookie. Повертає новий
// токен або null (cookie немає, протермінована чи недійсна).
export function refreshAccessToken(apiBase) {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(resolveBase(apiBase)).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Вхід: зберігає access- і CSRF-токени лише в пам'яті. Роль тут свідомо не
// повертається -- її бере AuthContext з GET /api/auth/me.
export async function loginRequest(apiBase, username, password) {
  const base = resolveBase(apiBase);
  const response = await fetch(`${base}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await readJson(response);
  if (!response.ok || !data?.token) {
    throw new Error(data?.message || "Помилка входу");
  }
  setAccessToken(data.token);
  if (data.csrfToken) csrfToken = data.csrfToken;
  return data.token;
}

// Профіль поточного користувача {id, username, role} або null.
export async function fetchMe(apiBase) {
  const header = getAuthHeader();
  if (!header) return null;
  try {
    const response = await fetch(`${resolveBase(apiBase)}/api/auth/me`, {
      method: "GET",
      credentials: "include",
      headers: { Authorization: header },
    });
    if (!response.ok) return null;
    const data = await readJson(response);
    if (!data?.role) return null;
    return { id: data.id, username: data.username, role: data.role };
  } catch {
    return null;
  }
}

// Вихід: сервер відкликає refresh-cookie. Best-effort -- локальний стан
// очищується в будь-якому разі.
export async function logoutRequest(apiBase) {
  const base = resolveBase(apiBase);
  try {
    await ensureCsrfToken(base);
    await postWithCsrf(base, "/api/auth/logout");
  } catch {
    // мережева помилка не має блокувати локальний вихід
  } finally {
    clearAuth();
  }
}

// Axios: на 401 одна спроба оновити токен і повторити початковий запит.
// Не застосовується до /api/auth/* (інакше можлива петля) і до вже
// повтореного запиту.
const AUTH_URL_RE = /\/api\/auth\//;

export async function retryAxiosOn401(error) {
  const config = error?.config;
  const status = error?.response?.status;
  if (
    status !== 401 ||
    !config ||
    config._authRetried ||
    AUTH_URL_RE.test(config.url || "")
  ) {
    throw error;
  }
  config._authRetried = true;
  const token = await refreshAccessToken();
  if (!token) throw error;

  const header = `Bearer ${token}`;
  if (typeof config.headers?.set === "function") {
    config.headers.set("Authorization", header);
  } else {
    config.headers = { ...config.headers, Authorization: header };
  }
  return axios.request(config);
}

if (axios.interceptors?.response) {
  axios.interceptors.response.use((response) => response, retryAxiosOn401);
}
